import { useRef, useState } from "react";
import { MessageCircle, Phone, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Captcha from "@/components/site/Captcha";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/lib/settings";
import { toast } from "sonner";

export default function FloatingActions() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", preferred_time: "", message: "" });
  const captchaRef = useRef(null);
  const { settings } = useSiteSettings();
  // Prefer the Contact Us phone number; fall back to the social.whatsapp override.
  // Strip everything except digits so WhatsApp's wa.me link works regardless of formatting.
  const contactDigits = String(settings?.contact_phone || "").replace(/\D/g, "");
  const socialDigits = String(settings?.social?.whatsapp || "").replace(/\D/g, "");
  const wa = contactDigits || socialDigits;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Name and phone are required");
      return;
    }
    const cap = captchaRef.current?.getValues() || {};
    setSubmitting(true);
    try {
      await api.post("/enquiries", { type: "callback", ...form, ...cap });
      toast.success("Request received. Our team will call you back shortly.");
      setForm({ name: "", phone: "", preferred_time: "", message: "" });
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
      captchaRef.current?.reset();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3" data-testid="floating-actions">
        {expanded && (
          <>
            {wa && (
              <a
                href={`https://wa.me/${wa}?text=${encodeURIComponent("Hi PharmacoHires, I'd like to talk about an opportunity.")}`}
                target="_blank"
                rel="noreferrer"
                data-testid="floating-whatsapp"
                className="flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-3 text-sm font-medium text-white shadow-lg transition-transform hover:-translate-y-0.5"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp Us
              </a>
            )}
            <button
              onClick={() => { setOpen(true); setExpanded(false); }}
              data-testid="floating-callback"
              className="flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-sm font-medium text-white shadow-lg transition-transform hover:-translate-y-0.5"
            >
              <Phone className="h-4 w-4" /> Request Callback
            </button>
          </>
        )}
        <button
          onClick={() => setExpanded((v) => !v)}
          aria-label="Toggle quick actions"
          data-testid="floating-actions-toggle"
          className="grid h-14 w-14 place-items-center rounded-full bg-secondary text-white shadow-xl transition-transform hover:-translate-y-0.5"
        >
          {expanded ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="callback-dialog">
          <DialogHeader>
            <DialogTitle>Request a callback</DialogTitle>
            <DialogDescription>Leave your number and a recruiter will get in touch.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="cb-name">Your name</Label>
              <Input id="cb-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="callback-name" />
            </div>
            <div>
              <Label htmlFor="cb-phone">Phone number</Label>
              <Input id="cb-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="callback-phone" />
            </div>
            <div>
              <Label htmlFor="cb-time">Preferred time</Label>
              <Input id="cb-time" placeholder="e.g. Today 4-6 PM" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} data-testid="callback-time" />
            </div>
            <div>
              <Label htmlFor="cb-msg">Message (optional)</Label>
              <Textarea id="cb-msg" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="callback-message" />
            </div>
            {open && <Captcha ref={captchaRef} testId="callback-captcha" />}
            <DialogFooter>
              <Button type="submit" disabled={submitting} data-testid="callback-submit" className="bg-primary hover:bg-primary/90">
                {submitting ? "Sending…" : "Request callback"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
